import { Dialog, Popover, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import {
  AiOutlineLock,
  AiOutlinePoweroff,
  AiOutlineSetting,
  AiOutlineRise,
} from "react-icons/ai";
import { BsWallet2 } from "react-icons/bs";
import { HiOutlineBars3 } from "react-icons/hi2";
import { IoAlertCircle, IoClose } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink } from "react-router-dom";
import { GiPresent } from "react-icons/gi";
import {
  mobileBottomNavLinks,
  mobileNavLinks,
  navLinks,
} from "../../data/navLinks";
import { logout, selectCurrentUser } from "../../features/auth/authSlice";
import { TwoFactorModal } from "./bitcoin-wallet/TwoFactorModal";
import { Logo } from "./Logo";
import { FaCrown, FaGraduationCap, FaUserShield } from "react-icons/fa";
import { useLogoutMutation } from "../../features/auth/authApiSlice";
import { BiSupport } from "react-icons/bi";
import { MdLeaderboard, MdPriceChange } from "react-icons/md";
import { AlertCircleIcon, UserPlus2 } from "lucide-react";
import { FiUserPlus } from "react-icons/fi";
import { GiTrade } from "react-icons/gi";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { FaTrophy } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { FlameIcon, StreakModal } from "./streak";
import { useGetStreakStatusQuery } from "../../features/streak/streakSlice";
import { useGetGiftBoxStatusQuery } from "../../features/treasury/treasurySlice";

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);

  const [logoutNow, { isLoading }] = useLogoutMutation();

  const user = useSelector(selectCurrentUser);
  
  // Fetch streak status
  const { data: streakStatus } = useGetStreakStatusQuery();
  // Fetch gift box status to show/hide Treasury Chest when feature is disabled
  const { data: giftBoxStatus } = useGetGiftBoxStatusQuery();
  const isTreasuryChestEnabled = giftBoxStatus?.enabled !== false;

  const visibleNavLinks = navLinks.filter(
    (link) => link.path !== "/app/treasury-chest" || isTreasuryChestEnabled
  );
  const visibleMobileNavLinks = mobileNavLinks.filter(
    (link) => link.path !== "/app/treasury-chest" || isTreasuryChestEnabled
  );
  const visibleMobileBottomNavLinks = mobileBottomNavLinks.filter(
    (link) => link.path !== "/app/treasury-chest" || isTreasuryChestEnabled
  );

  function toggleModal() {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  }

  const dispatch = useDispatch();

  const handleLogout = async () => {
    await logoutNow().unwrap();
    dispatch(logout());
  };

  return (
    <div className="w-full sticky lg:px-4 z-40">
      <div className="container-lg px-4 lg:px-14 md:bg-black py-4 rounded-bl-lg rounded-br-lg flex justify-between items-center">
        {/* Logo or mobile menu ---------------*/}
        <Logo />
        <HiOutlineBars3
          className="text-3xl md:hidden text-white hover:text-white/80 cursor-pointer"
          onClick={toggleModal}
        />

        <Transition appear show={isMobileMenuOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative"
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <div className="fixed font-dashboard w-full inset-0 z-50">
              <div className="flex min-h-full">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full h-screen transform overflow-y-auto bg-gray-500 p-4 text-left align-middle shadow-xl transition-all">
                    <div className="p-2 w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-offset-2 ring-offset-gray-500 ring-indigo-500 bg-indigo-300 flex justify-center items-center">
                            <p className="text-2xl text-white font-bold">
                              {user?.name?.charAt(0)}
                            </p>
                          </div>
                          {/* Mobile Streak Flame */}
                          {streakStatus?.isFeatureEnabled && (
                            <FlameIcon
                              streakCount={streakStatus.currentStreak}
                              redeemAvailable={streakStatus.redeemAvailable}
                              isEnabled={streakStatus.isFeatureEnabled}
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                setIsStreakModalOpen(true);
                              }}
                            />
                          )}
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              Hello,{" "}
                            </h3>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-light text-white/60">
                                {user?.name}
                              </p>
                              {user?.badges?.includes('VIP_VERIFIED') && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded font-medium">
                                  <FaCrown className="w-2.5 h-2.5" />
                                  VIP
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <IoClose
                          className="text-2xl text-white cursor-pointer"
                          onClick={() => setIsMobileMenuOpen(false)}
                        />
                      </div>

                      <hr className="border-white/10 w-full mt-4" />

                      <div className="flex flex-col gap-4 w-full mt-8 pb-32">
                        {visibleMobileNavLinks.map((link, index) => (
                          <NavLink
                            key={index}
                            to={link.path}
                            className={({ isActive }) =>
                              isActive
                                ? "px-4 py-3 text-primary bg-black flex gap-2 items-center border-l-2 border-primary"
                                : "px-4 py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex gap-2 items-center border-l-2 border-transparent"
                            }
                            onClick={toggleModal}
                          >
                            {link.icon}

                            <div className="flex items-center gap-1 flex-1">
                              <p className="text-inherit text-xs mt-1">
                                {link.name}
                              </p>
                              {link.isNew && (
                                <span className="bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded mt-1 font-medium">
                                  NEW
                                </span>
                              )}
                            </div>
                          </NavLink>
                        ))}

                        <NavLink
                          to="/app/transaction-history"
                          className={({ isActive }) =>
                            isActive
                              ? "px-4 py-3 text-primary bg-black flex gap-2 items-center border-l-2 border-primary"
                              : "px-4 py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex gap-2 items-center border-l-2 border-transparent"
                          }
                          onClick={toggleModal}
                        >
                          <HiOutlineDocumentReport className="text-2xl" />
                          <p className="text-inherit text-xs">Transactions Report</p>
                        </NavLink>

                        <Link
                          to="/app/email-subscription"
                          className="px-4 py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex gap-2 items-center border-l-2 border-transparent"
                          onClick={toggleModal}
                        >
                          <HiOutlineMail className="text-2xl" />
                          <p className="text-inherit text-xs">Get Email Updates on Deals</p>
                        </Link>

                        <NavLink
                          to="/app/analytics"
                          className={({ isActive }) =>
                            isActive
                              ? "px-4 py-3 text-primary bg-black flex gap-2 items-center border-l-2 border-primary"
                              : "px-4 py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex gap-2 items-center border-l-2 border-transparent"
                          }
                          onClick={toggleModal}
                        >
                          <AiOutlineRise className="text-2xl" />
                          <p className="text-inherit text-xs">Analytics</p>
                        </NavLink>

                        <button
                          className="px-4 py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex gap-2 items-center border-l-2 border-transparent"
                          onClick={handleLogout}
                        >
                          <AiOutlinePoweroff className="text-2xl" />
                          <p className="text-inherit text-xs">Logout</p>
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
        {/* Logo or mobile menu end---------------*/}

        <div className="hidden md:flex md:items-center md:gap-5 xl:gap-8 flex-1 justify-center ml-12 xl:ml-52">
          {visibleNavLinks.map((link, index) => (
            <NavLink
              key={index}
              to={link.path}
              className={({ isActive }) =>
                isActive
                  ? "text-primary flex flex-col items-center border-b-2 border-primary py-2 relative"
                  : "transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex flex-col items-center border-b-2 border-transparent relative"
              }
            >
              <div className="relative">
                {link.icon}
                {link.isNew && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[7px] px-1 py-0.5 rounded-full font-semibold leading-none whitespace-nowrap">
                    NEW
                  </span>
                )}
              </div>

              <p className="text-inherit text-xs mt-1">{link.name}</p>
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          <Link to="/app/withdraw">
            <button className="btn-primary px-4 py-2 hidden lg:flex">
              Withdraw Now
            </button>
          </Link>

          {/* Two factor modal is here ------------------------------------------------- */}
          <TwoFactorModal />
          {/* Two factor modal is here ------------------------------------------------- */}

          {/* <Popover as='div' className='relative'>
            <Popover.Button className='flex items-center'>
              <div
                className='relative p-2 rounded-xl lg:rounded-lg bg-white/10 lg:bg-gray-100 cursor-pointer'
                onClick={() => setIsNotificationOpen(true)}
              >
                <svg
                  width='26'
                  height='26'
                  viewBox='0 0 24 26'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M3.2001 16.5897H3.78984V16V8.8C3.78984 4.2656 7.4657 0.589744 12.0001 0.589744C16.5345 0.589744 20.2104 4.2656 20.2104 8.8V16V16.5897H20.8001H21.8104V17.0103H2.18984V16.5897H3.2001ZM8.58984 20V19.7897H15.4104V20C15.4104 21.8834 13.8835 23.4103 12.0001 23.4103C10.1167 23.4103 8.58984 21.8834 8.58984 20Z'
                    fill='white'
                    stroke='white'
                    stroke-width='1.17949'
                  />
                </svg>
                
                <div className='w-2 h-2 rounded-full bg-red-500 absolute top-0 right-0'></div>{' '}
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter='transition ease-in duration-200'
              enterFrom='opacity-0 translate-y-1'
              enterTo='opacity-100 translate-y-0'
              leave='transition ease-linear duration-150'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-1'
            >
              <Popover.Panel className='absolute z-10 top-[3.5rem] w-[300px] right-0 border border-white/10 bg-black shadow-xl shadow-black rounded-lg py-2'>
                {({ close }) => (
                  <div className='max-w-3xl h-[500px] mx-auto p-4'>
                    <div className='flex items-center justify-between'>
                      <h2 className='text-xl font-bold text-white'>
                        Notifications
                      </h2>
                      <IoClose
                        className='text-2xl text-white cursor-pointer'
                        onClick={() => close()}
                      />
                    </div>

                    <div className='flex flex-col gap-2 divide-y divide-white/10 mt-8'>
                      <div className='flex flex-col gap-1 py-2'>
                        <h3 className='text-white text-sm'>
                          ⚡ You withdrew 200 NGN via ...
                        </h3>
                        <p className='text-white/30 text-xs'>
                          You can enter your Bitcoin or USDT address as well in
                          the..
                          <p className='mt-4 text-xs text-white/30'>
                            03 May 2022 at 4:30pm
                          </p>
                        </p>
                      </div>
                      <div className='flex flex-col gap-1 py-2'>
                        <h3 className='text-white text-sm'>
                          Important Announcemnet For...
                        </h3>
                        <p className='text-white/30 text-xs'>
                          You can enter your Bitcoin or USDT address as well in
                          the..
                          <p className='mt-4 text-xs text-white/30'>
                            03 May 2022 at 4:30pm
                          </p>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </Popover> */}

          <div className="flex items-center gap-3">
          {/* Treasury Chest Icon - Desktop (hidden when feature disabled) */}
          {isTreasuryChestEnabled && (
            <Link
              to="/app/treasury-chest"
              className="relative p-2 rounded-xl lg:rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
              title="Treasury Chest"
            >
              <GiPresent className="text-white text-2xl" />
            </Link>
          )}
          {/* Streak Flame Icon - Desktop */}
          {streakStatus?.isFeatureEnabled && (
            <FlameIcon
              streakCount={streakStatus.currentStreak}
              redeemAvailable={streakStatus.redeemAvailable}
              isEnabled={streakStatus.isFeatureEnabled}
              onClick={() => setIsStreakModalOpen(true)}
            />
          )}
          
          <Popover as="div" className="relative hidden md:block">
            <Popover.Button className="flex items-center">
              <div className="flex items-center gap-3 cursor-pointer">
                <div className="h-12 w-12 rounded-lg border-2 border-pink-400 overflow-hidden bg-indigo-300 text-3xl font-bold text-white flex items-center justify-center">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="hidden 2xl:flex lg:flex-col 2xl:gap-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white text-left text-sm">{user?.name}</h3>
                    {user?.badges?.includes('VIP_VERIFIED') && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded font-medium">
                        <FaCrown className="w-2.5 h-2.5" />
                        VIP
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs">{user?.email}</p>
                </div>
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-in duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-linear duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 top-[3.6rem] w-[250px] right-0 border border-white/10 bg-black shadow-xl shadow-black rounded-lg py-2">
                {({ close }) => (
                  <div className="flex flex-col">
                    <Link to="/app/withdraw" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <BsWallet2 className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Withdraw now</p>
                      </div>
                    </Link>

                    <Link
                      to="/app/account/two-factor-security"
                      onClick={() => close()}
                    >
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <AiOutlineLock className="text-white text-xl" />
                        <p className="text-white/80 text-sm">2FA Security</p>
                      </div>
                    </Link>

                    <hr className="border-gray-600" />

                    <Link to="/app/account/basic-info" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <AiOutlineSetting className="text-white text-xl" />
                        <p className="text-white/80 text-sm">
                          Account Settings
                        </p>
                      </div>
                    </Link>

                    <Link to="/app/transaction-history" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <HiOutlineDocumentReport className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Transactions Report</p>
                      </div>
                    </Link>

                    <Link to="/app/levels" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <FaTrophy className="text-white text-xl" />
                        <p className="text-white/80 text-sm">My Level</p>
                      </div>
                    </Link>

                    <Link to="/app/leaderboards" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <MdLeaderboard className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Leaderboards</p>
                      </div>
                    </Link>

                    <Link to="/app/referrals" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <FiUserPlus className="text-white text-xl" />
                        <p className="text-white/80 text-sm">
                          Refer your friends
                        </p>
                      </div>
                    </Link>

                    <Link to="/app/rate-alerts" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <IoAlertCircle className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Rate Alerts</p>
                      </div>
                    </Link>

                    {isTreasuryChestEnabled && (
                      <Link to="/app/treasury-chest" onClick={() => close()}>
                        <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                          <GiPresent className="text-white text-xl" />
                          <p className="text-white/80 text-sm">TREASURY CHEST</p>
                        </div>
                      </Link>
                    )}

                    <Link to="/app/support" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <BiSupport className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Help & Support</p>
                      </div>
                    </Link>
                    <Link to="/app/tutorial" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <FaGraduationCap className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Tutorial</p>
                      </div>
                    </Link>
                    <Link to="/app/account/kyc" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <FaUserShield className="text-white text-xl" />
                        <p className="text-white/80 text-sm">
                          KYC Verification
                        </p>
                      </div>
                    </Link>
                    <Link to="/app/email-subscription" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <HiOutlineMail className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Get Email Updates on Deals</p>
                      </div>
                    </Link>
                    <Link to="/app/analytics" onClick={() => close()}>
                      <div className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500">
                        <AiOutlineRise className="text-white text-xl" />
                        <p className="text-white/80 text-sm">Analytics</p>
                      </div>
                    </Link>
                    <button
                      className="transition-all duration-200 flex items-center gap-3 cursor-pointer px-4 py-3 hover:bg-gray-500"
                      onClick={handleLogout}
                    >
                      <AiOutlinePoweroff className="text-white text-xl" />
                      <p className="text-white/80 text-sm">Logout</p>
                    </button>
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </Popover>
          </div>

          {/* Mobile bottom nav ------------------------------------------------------------------------------------------------------ */}
          <div className="flex items-center justify-between gap-4 px-8 md:hidden fixed bottom-0 left-0 right-0 bg-black w-full z-40 overflow-scroll">
            {visibleMobileBottomNavLinks.map((link, index) => (
              <NavLink
                key={index}
                to={link.path}
                className={({ isActive }) =>
                  isActive
                    ? "py-3 text-primary flex flex-col items-center border-t-2 border-primary relative"
                    : "py-3 transition-all duration-200 ease-in text-gray-200 hover:text-gray-300 flex flex-col items-center border-t-2 border-transparent relative"
                }
              >
                {link.icon}

                <div className="flex items-center gap-1">
                  <p className="text-inherit text-xs mt-1">{link.name}</p>
                  {link.isNew && (
                    <span className="bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded mt-1 font-medium">
                      NEW
                    </span>
                  )}
                </div>
              </NavLink>
            ))}
          </div>
          {/* Mobile bottom nav end------------------------------------------------------------------------------------------------------ */}
        </div>
      </div>
      
      {/* Streak Modal */}
      {streakStatus && (
        <StreakModal
          isOpen={isStreakModalOpen}
          onClose={() => setIsStreakModalOpen(false)}
          status={streakStatus}
        />
      )}
    </div>
  );
};
